import { type RouteRecordRaw } from "vue-router"

import { type AddressTypes, type Permission } from "@/types"
import { StepKey } from "@/store/child"

function generateAddressTypes(...types: (keyof AddressTypes)[]): AddressTypes {
  const addressTypes: AddressTypes = {}

  types.forEach((key) => {
    addressTypes[key] = true
  })

  return addressTypes
}

const routes: RouteRecordRaw[] = [
  {
    path: "/kinder",
    name: "ChildrenPage",
    component: () => import("./children/ChildrenPage.vue"),
    meta: {
      requiredRoles: <Permission[]>["child:list", "child:unlock"],
    },
  },
  {
    path: "/buchungen",
    name: "BookingsPage",
    component: () => import("./bookings/BookingsPage.vue"),
    meta: {
      requiredRoles: <Permission[]>["booking:list", "booking:unlock"],
    },
  },
  {
    name: "ChildCreate",
    path: "/kinder/erstellen",
    redirect: {
      name: "ChildCreateFormContract",
    },
    component: () => import("./children/views/ChildCreate.vue"),
    meta: {
      requiredRoles: <Permission[]>["child:create", "child:unlock"],
    },
    children: [
      {
        path: "vertrag",
        component: () => import("./children/views/ChildDraftForm.vue"),
        props: { step: "contract" },
        children: [
          {
            name: "ChildCreateFormContract",
            path: "",
            component: () => import("./children/forms/FormContract.vue"),
          },
        ],
      },
      {
        path: "allgemein",
        component: () => import("./children/views/ChildDraftForm.vue"),
        props: { step: "general" },
        children: [
          {
            name: "ChildCreateFormGeneral",
            path: "",
            component: () => import("./children/forms/FormGeneral.vue"),
          },
        ],
      },
      {
        path: "verschiedenes",
        component: () => import("./children/views/ChildDraftForm.vue"),
        props: { step: "miscellaneous" },
        children: [
          {
            name: "ChildCreateFormMiscellaneous",
            path: "",
            component: () => import("./children/forms/FormMiscellaneous.vue"),
            meta: {
              allowServiceList: true,
            },
            children: [
              {
                name: "CreateChildCreateInstitutionSpecialServiceDialog",
                path: "fachdienst/erstellen",
                props: {
                  addressTypes: generateAddressTypes("specialService"),
                },
                meta: {
                  requiredRoles: <Permission[]>["address:create"],
                },
                component: () => import("./children/forms/dialogs/CreateAddressDialog.vue"),
                children: [
                  {
                    path: "kategorie",
                    component: () => import("../stammdaten/categories/CategoryDialog.vue"),
                    meta: {
                      requiredRoles: <Permission[]>["category:create"],
                    },
                  },
                ],
              },
              {
                name: "CreateChildCreateDoctorDialog",
                path: "hausarzt/erstellen",
                props: {
                  addressTypes: generateAddressTypes("doctor"),
                },
                meta: {
                  requiredRoles: <Permission[]>["address:create"],
                },
                component: () => import("./children/forms/dialogs/CreateAddressDialog.vue"),
                children: [
                  {
                    path: "kategorie",
                    component: () => import("../stammdaten/categories/CategoryDialog.vue"),
                    meta: {
                      requiredRoles: <Permission[]>["category:create"],
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "impfstatus",
        component: () => import("./children/views/ChildDraftForm.vue"),
        props: { step: "vaccination" },
        children: [
          {
            name: "ChildCreateFormVaccination",
            path: "",
            component: () => import("./children/forms/FormVaccination.vue"),
          },
        ],
      },
      ...[StepKey.FirstGuardian, StepKey.SecondGuardian, StepKey.AdditionalGuardian].map(
        (step, index) => ({
          name: `Bezugsperson${index}`,
          path: `bezugsperson-${index}`,
          component: () => import("./children/views/ChildDraftForm.vue"),
          props: { step },
          children: [
            {
              name: `ChildCreateFormLocalGuardian${index}`,
              path: "",
              component: () => import("./children/forms/FormLocalGuardian.vue"),
              props: { index },
              meta: {
                allowServiceList: true,
              },
              children: [
                {
                  name: `CreateEmployerDialog${index}`,
                  path: "arbeitgeber/erstellen",
                  props: {
                    addressTypes: generateAddressTypes("employer"),
                  },
                  meta: {
                    requiredRoles: <Permission[]>["address:create"],
                  },
                  component: () => import("./children/forms/dialogs/CreateAddressDialog.vue"),
                  children: [
                    {
                      name: `CreateEmployerDialogCreateCategoryDialog${index}`,
                      path: "kategorie",
                      component: () => import("../stammdaten/categories/CategoryDialog.vue"),
                      meta: {
                        requiredRoles: <Permission[]>["category:create"],
                      },
                    },
                  ],
                },
                {
                  name: `CreateCategoryDialog${index}`,

                  path: "kategorie",
                  component: () => import("../stammdaten/address/CreateCategoryDialog.vue"),
                  meta: {
                    requiredRoles: <Permission[]>["category:create"],
                  },
                },
              ],
            },
          ],
        })
      ),
      {
        path: "gruppen",
        component: () => import("./children/views/ChildDraftForm.vue"),
        props: { step: "group" },
        children: [
          {
            path: "erstellen",
            name: "CreateChildGroupCreate",
            component: () => import("./children/forms/ManageGroup.vue"),
            meta: {
              requiredRoles: <Permission[]>["group:unlock"],
            },
          },
        ],
      },
      {
        path: "buchung",
        component: () => import("./children/views/ChildDraftForm.vue"),
        props: { step: "reservation" },
        children: [
          {
            path: "erstellen",
            name: "ChildCreateFormReservationCreate",
            component: () => import("./children/forms/FormReservation.vue"),
            children: [
              {
                path: "schule/erstellen",
                name: "ChildCreateReservationFormCreateSchoolDialogCreate",
                component: () => import("../stammdaten/school/SchoolDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["school:create"],
                },
              },
            ],
            meta: {
              requiredRoles: <Permission[]>[
                "booking:create",
                "booking:unlock",
                "contract:update",
                "child:update",
              ],
            },
          },
        ],
      },
      {
        path: "gebuehren",
        component: () => import("./children/views/ChildDraftForm.vue"),
        props: { step: "fee" },
        children: [
          {
            name: "ChildCreateFormFee",
            path: "",
            component: () => import("./children/forms/FormFee.vue"),
            children: [
              {
                name: "CreateMainDebtorForNewChildDialog",
                path: "hauptbeitragzahler/erstellen",
                component: () => import("./children/forms/dialogs/fee/MainDebtorDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["contract:update"],
                },
              },

              {
                name: "EditMainDebtorForNewChildDialog",
                path: "hauptbeitragzahler/:mainDebtorId",
                component: () => import("./children/forms/dialogs/fee/MainDebtorDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["contract:update"],
                },
              },
              {
                name: "CreateAdditionalDebtorForNewChildDialog",
                path: "zusaetzlicher-beitragzahler/erstellen",
                component: () => import("./children/forms/dialogs/fee/AdditionalDebtorDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["contract:update"],
                },
              },
              {
                name: "EditAdditionalDebtorForNewChildDialog",
                path: "zusaetzlicher-beitragzahler/:debtorId",
                component: () => import("./children/forms/dialogs/fee/AdditionalDebtorDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["contract:update"],
                },
              },
              {
                name: "CreateExternDebtorForNewChildDialog",
                path: "externe-kostenuebernahme/erstellen",
                component: () => import("./children/forms/dialogs/fee/AdditionalDebtorDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["contract:update"],
                },
              },
              {
                name: "EditExternDebtorForNewChildDialog",
                path: "externe-kostenuebernahme/:debtorId",
                component: () => import("./children/forms/dialogs/fee/AdditionalDebtorDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["contract:update"],
                },
              },
              {
                name: "CreateContractFeeForNewChildDialog",
                path: "beitrag/erstellen",
                component: () => import("./children/forms/fee/ContractFeeDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["contract:update", "fee:update"],
                },
              },
              {
                name: "EditContractFeeForNewChildDialog",
                path: "beitrag/:categoryFeeTypeId",
                component: () => import("./children/forms/fee/ContractFeeDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["contract:update", "fee:update"],
                },
              },
              {
                name: "CreateContractDraftForNewChildDialog",
                path: "vorlage/erstellen",
                component: () => import("./children/forms/fee/ContractDraftDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["contract:update"],
                },
              },
            ],
          },
        ],
      },
      {
        path: "vertrag",
        component: () => import("./children/views/ChildDraftForm.vue"),
        props: { step: "print" },
        children: [
          {
            name: "ChildCreatePrintContract",
            path: "",
            component: () => import("./children/forms/PrintContract.vue"),
          },
        ],
      },
      {
        name: "ChildCreateConclusion",
        path: "abschluss",
        component: () => import("./children/views/ChildCreateConclusion.vue"),
      },
    ],
  },
  {
    name: "ChildEdit",
    path: "/kinder/:id",
    redirect: (to) => ({ name: "ChildEditFormGeneral", params: { id: to.params.id } }),
    component: () => import("./children/views/ChildView.vue"),
    meta: {
      requiredRoles: <Permission[]>["child:view", "child:unlock"],
    },
    children: [
      {
        name: "ChildEditFormGeneral",
        path: "allgemein",
        component: () => import("./children/forms/FormGeneral.vue"),
      },
      {
        name: "ChildEditFormMiscellaneous",
        path: "verschiedenes",
        meta: {
          allowServiceList: true,
        },
        component: () => import("./children/forms/FormMiscellaneous.vue"),
        children: [
          {
            name: "ChildEditInstitutionSpecialServiceDialog",
            path: "hausarzt/erstellen",
            props: {
              addressTypes: generateAddressTypes("doctor"),
            },
            component: () => import("./children/forms/dialogs/CreateAddressDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["address:create"],
            },
            children: [
              {
                path: "kategorie",
                component: () => import("../stammdaten/categories/CategoryDialog.vue"),
                props: { isCreateDialog: true },
                meta: {
                  requiredRoles: <Permission[]>["category:create"],
                },
              },
            ],
          },
          {
            name: "ChildEditDoctorDialog",
            path: "fachdienst/erstellen",
            props: {
              addressTypes: generateAddressTypes("specialService"),
            },
            component: () => import("./children/forms/dialogs/CreateAddressDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["address:create"],
            },
            children: [
              {
                path: "kategorie",
                props: { isCreateDialog: true },
                component: () => import("../stammdaten/categories/CategoryDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["category:create"],
                },
              },
            ],
          },
        ],
      },
      {
        name: "ChildEditFormVaccination",
        path: "impfstatus",
        component: () => import("./children/forms/FormVaccination.vue"),
      },
      {
        name: "ChildEditFormLocalGuardian",
        path: "bezugsperson",
        component: () => import("./children/forms/ManageCustodians.vue"),
        meta: {
          requiredRoles: <Permission[]>["child:list"],
        },
      },
      {
        name: "ChildEditFormSiblings",
        path: "geschwister",
        component: () => import("./children/forms/ManageSiblings.vue"),
        children: [
          {
            name: "NeueGeschwister",
            path: "erstellen",
            component: () => import("./children/forms/dialogs/NewSiblingDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["child:update"],
            },
          },
          {
            name: "EditExternalSibling",
            path: "external/:externalId",
            component: () => import("./children/forms/dialogs/EditExternalSiblingDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["child:update"],
            },
          },
        ],
      },
      {
        path: "bezugsperson/erstellen",
        name: "UpdateChildCreateCustodian",
        component: () => import("./children/forms/FormLocalGuardian.vue"),
        meta: {
          requiredRoles: <Permission[]>["child:create"],
          allowServiceList: true,
        },
        children: [
          {
            name: "UpdateChildCreateCustodianCreateEmployer",
            path: "arbeitgeber/erstellen",
            props: {
              addressTypes: generateAddressTypes("employer"),
            },
            meta: {
              requiredRoles: <Permission[]>["address:create"],
            },
            component: () => import("./children/forms/dialogs/CreateAddressDialog.vue"),
            children: [
              {
                path: "kategorie",
                component: () => import("../stammdaten/categories/CategoryDialog.vue"),
                meta: {
                  requiredRoles: <Permission[]>["category:create"],
                },
              },
            ],
          },
          {
            path: "kategorie",
            component: () => import("../stammdaten/categories/CategoryDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["category:create"],
            },
          },
        ],
      },
      {
        name: "ChildEditFormLocalGuardianEdit",
        path: "bezugsperson/:bid",
        component: () => import("./children/forms/FormLocalGuardian.vue"),
        meta: { allowServiceList: true },
        children: [
          {
            name: "ChildEditFormLocalGuardianEditCreateEmployer",
            path: "arbeitgeber/erstellen",
            props: {
              addressTypes: generateAddressTypes("employer"),
            },
            meta: {
              requiredRoles: <Permission[]>["address:create"],
            },
            component: () => import("./children/forms/dialogs/CreateAddressDialog.vue"),
            children: [
              {
                name: "ChildEditFormLocalGuardianEditCreateEmployerCreateCategory",
                path: "kategorie",
                component: () => import("../stammdaten/categories/CategoryDialog.vue"),
                props: { isCreateDialog: true },

                meta: {
                  requiredRoles: <Permission[]>["category:create"],
                },
              },
            ],
          },
          {
            name: "ChildEditFormLocalGuardianEditCreateCategory",
            path: "kategorie",
            component: () => import("../stammdaten/categories/CategoryDialog.vue"),
            props: { isCreateDialog: true },

            meta: {
              requiredRoles: <Permission[]>["category:create"],
            },
          },
        ],
      },
      {
        name: "ChildEditFormContract",
        path: "vertrag/:contractId",
        component: () => import("./children/forms/FormContract.vue"),
        children: [
          {
            name: "AddNewContract",
            path: "vertrag/erstellen",
            component: () => import("./children/forms/dialogs/CreateContractDialog.vue"),
          },
        ],
      },
      {
        name: "ChildEditFormGroups",
        path: ":contractId/gruppen",
        component: () => import("./children/forms/FormGroups.vue"),
      },
      {
        path: ":contractId/gruppen/erstellen",
        name: "ChildGroupCreate",
        component: () => import("./children/forms/ManageGroup.vue"),
        meta: {
          requiredRoles: <Permission[]>["group:unlock"],
        },
      },
      {
        path: ":contractId/gruppen/:childGroupId",
        name: "ChildGroupEdit",
        component: () => import("./children/forms/ManageGroup.vue"),
        meta: {
          requiredRoles: <Permission[]>["group:unlock"],
        },
      },
      {
        name: "ChildEditFormReservations",
        path: "buchung/:contractId",
        component: () => import("./children/forms/ManageReservations.vue"),
      },
      {
        path: "buchung/erstellen/:contractId",
        name: "ChildEditFormReservationCreate",
        component: () => import("./children/forms/FormReservation.vue"),
        meta: {
          requiredRoles: <Permission[]>["booking:create", "booking:unlock"],
        },
        children: [
          {
            path: "schule/erstellen",
            name: "ChildEditReservationFormCreateSchoolDialogCreate",
            component: () => import("../stammdaten/school/SchoolDialog.vue"),
            props: { isCreateDialog: true },
            meta: {
              requiredRoles: <Permission[]>["school:create"],
            },
          },
        ],
      },
      {
        path: "buchung/:contractId/:childBookingId",
        name: "ChildEditFormReservationEdit",
        component: () => import("./children/forms/FormReservation.vue"),
        meta: {
          requiredRoles: <Permission[]>["booking:view", "booking:unlock"],
        },
        children: [
          {
            path: "schule/erstellen",
            name: "ChildEditReservationFormCreateSchoolDialogCreate",
            props: { isCreateDialog: true },
            component: () => import("../stammdaten/school/SchoolDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["school:create"],
            },
          },
        ],
      },
      {
        name: "ChildReservationCalendar",
        path: "buchungskalender/:contractId",
        component: () => import("./children/views/BookingCalendar.vue"),
      },
      {
        name: "ChildEditFormFee",
        path: "gebuehren/:contractId",
        component: () => import("./children/forms/FormFee.vue"),
        meta: {
          requiredRoles: <Permission[]>["contract:view"],
        },
        children: [
          {
            name: "CreateMainDebtorDialog",
            path: "hauptbeitragzahler/erstellen",
            component: () => import("./children/forms/dialogs/fee/MainDebtorDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["contract:update"],
            },
          },
          {
            name: "EditMainDebtorDialog",
            path: "hauptbeitragzahler/:mainDebtorId",
            component: () => import("./children/forms/dialogs/fee/MainDebtorDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["contract:update"],
            },
          },
          {
            name: "CreateAdditionalDebtorDialog",
            path: "zusaetzlicher-beitragzahler/erstellen",
            component: () => import("./children/forms/dialogs/fee/AdditionalDebtorDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["contract:update"],
            },
          },
          {
            name: "EditAdditionalDebtorDialog",
            path: "zusaetzlicher-beitragzahler/:debtorId",
            component: () => import("./children/forms/dialogs/fee/AdditionalDebtorDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["contract:update"],
            },
          },
          {
            name: "CreateExternDebtorDialog",
            path: "externe-kostenuebernahme/erstellen",
            component: () => import("./children/forms/dialogs/fee/AdditionalDebtorDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["contract:update"],
            },
          },
          {
            name: "EditExternDebtorDialog",
            path: "externe-kostenuebernahme/:debtorId",
            component: () => import("./children/forms/dialogs/fee/AdditionalDebtorDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["contract:update"],
            },
          },
          {
            name: "CreateContractFeeDialog",
            path: "beitrag/erstellen",
            component: () => import("./children/forms/fee/ContractFeeDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["contract:update", "fee:update"],
            },
          },
          {
            name: "EditContractFeeDialog",
            path: "beitrag/:categoryFeeTypeId",
            component: () => import("./children/forms/fee/ContractFeeDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["contract:update", "fee:update"],
            },
          },
          {
            name: "CreateContractDraftDialog",
            path: "vorlage/erstellen",
            component: () => import("./children/forms/fee/ContractDraftDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["contract:update"],
            },
          },
        ],
      },
      {
        name: "ChildEditPrintContract",
        path: "vertrag-drucken/:contractId",
        component: () => import("./children/forms/PrintContract.vue"),
      },
    ],
  },
  {
    path: "/kinder-ansicht",
    name: "ChildrenListView",
    component: () => import("./children/views/ChildrenListView.vue"),
  },
  {
    path: "/anwesenheit",
    name: "PresencePage",
    component: () => import("./children/views/PresencePage.vue"),
    meta: {
      requiredRoles: <Permission[]>["child:list", "child:unlock"],
    },
  },
  {
    path: "/check-in",
    name: "CheckInPage",
    component: () => import("./children/views/CheckInPage.vue"),
    meta: {
      requiredRoles: <Permission[]>["child:list", "child:unlock"],
    },
  },
  {
    path: "/belegungsplan",
    name: "AttendancePage",
    component: () => import("./children/views/AttendancePage.vue"),
    meta: {
      requiredRoles: <Permission[]>["child:list", "child:unlock"],
    },
  },
  {
    path: "/gruppen",
    name: "GroupPage",
    component: () => import("./groups/GroupPage.vue"),
    meta: {
      requiredRoles: <Permission[]>["group:list", "group:unlock"],
    },
  },
  {
    path: "/gruppen/erstellen",
    name: "GroupViewCreate",
    component: () => import("./groups/GroupView.vue"),
    meta: {
      requiredRoles: <Permission[]>["group:create", "group:unlock"],
    },
    children: [
      {
        path: "",
        name: "GroupFormCreate",
        component: () => import("./groups/GroupForm.vue"),
        children: [
          {
            path: "management/erstellen",
            name: "GroupCreateManagementFormCreate",
            component: () => import("./groups/ManagementDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["group:update", "employee:update"],
            },
          },
          {
            path: "management/:managementId",
            name: "GroupCreateManagementFormEdit",
            component: () => import("./groups/ManagementDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["employee:view"],
            },
          },
        ],
      },
    ],
  },
  {
    path: "/gruppen/:id",
    name: "GroupViewEdit",
    redirect: (to) => ({ name: "GroupFormEdit", params: { id: to.params.id } }),
    component: () => import("./groups/GroupView.vue"),
    meta: {
      requiredRoles: <Permission[]>["group:view", "group:unlock"],
    },
    children: [
      {
        path: "",
        name: "GroupFormEdit",
        component: () => import("./groups/GroupForm.vue"),
        children: [
          {
            path: "management/erstellen",
            name: "GroupEditManagementFormCreate",
            component: () => import("./groups/ManagementDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["group:update", "employee:update"],
            },
          },
          {
            path: "management/:managementId",
            name: "GroupEditManagementFormEdit",
            component: () => import("./groups/ManagementDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["employee:view"],
            },
          },
        ],
      },
    ],
  },
  {
    path: "/inbox",
    name: "InboxPage",
    component: () => import("./inbox/InboxPage.vue"),
    meta: {
      requiredRoles: <Permission[]>["inbox_child:list", "inbox_child:unlock"],
    },
  },
]

export default routes
